import { useState } from "react";
import { useAdminUsers, useAddAdminUser, useUpdateAdminUser, type AdminRole } from "@/hooks/useAdminUsers";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserPlus, Shield, Crown, Eye, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

const ROLES: { value: AdminRole; label: string; icon: React.ElementType }[] = [
  { value: "super_admin", label: "Super Admin", icon: Crown },
  { value: "support_agent", label: "Support Agent", icon: Shield },
  { value: "billing_admin", label: "Billing Admin", icon: CreditCard },
  { value: "read_only", label: "Read Only", icon: Eye },
];

const roleColor: Record<string, string> = {
  super_admin: "bg-primary text-primary-foreground",
  support_agent: "bg-secondary text-secondary-foreground",
  billing_admin: "bg-accent text-accent-foreground",
  read_only: "bg-muted text-muted-foreground",
};

const PlatformTeam = () => {
  const { data: admins = [], isLoading } = useAdminUsers();
  const { can } = useAdminRole();
  const addAdmin = useAddAdminUser();
  const updateAdmin = useUpdateAdminUser();

  const [addOpen, setAddOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AdminRole>("read_only");

  const canManage = can("invite_admins");

  const handleAdd = async () => {
    if (!email.trim()) return;
    try {
      await addAdmin.mutateAsync({ email: email.trim(), role });
      toast.success(`Added ${email.trim()} as ${role.replace("_", " ")}`);
      setAddOpen(false);
      setEmail("");
      setRole("read_only");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to add admin");
    }
  };

  const handleRoleChange = async (id: string, newRole: string) => {
    try {
      await updateAdmin.mutateAsync({ id, role: newRole });
      toast.success("Role updated");
    } catch {
      toast.error("Failed to update role");
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await updateAdmin.mutateAsync({ id, status: newStatus });
      toast.success(`Admin ${newStatus === "active" ? "activated" : "deactivated"}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="space-y-6">
      <SEO title="Admin Team" description="Manage platform admin team" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Team</h1>
          <p className="text-sm text-muted-foreground">Manage platform administrators and their roles</p>
        </div>
        {canManage && (
          <Button onClick={() => setAddOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" /> Add Admin
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Added</TableHead>
                {canManage && <TableHead className="w-[140px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.email}</TableCell>
                  <TableCell>
                    {canManage ? (
                      <Select value={a.role} onValueChange={(v) => handleRoleChange(a.id, v)}>
                        <SelectTrigger className="w-[160px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => (
                            <SelectItem key={r.value} value={r.value}>
                              {r.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${roleColor[a.role] ?? "bg-muted text-muted-foreground"} text-xs capitalize`}>
                        {a.role.replace("_", " ")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.status === "active" ? "default" : "secondary"} className="text-xs capitalize">
                      {a.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(a.created_at).toLocaleDateString()}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => handleToggleStatus(a.id, a.status)}
                      >
                        {a.status === "active" ? "Deactivate" : "Activate"}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {admins.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManage ? 5 : 4} className="text-center text-muted-foreground py-8">
                    No admin users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add Admin Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Platform Admin</DialogTitle>
            <DialogDescription>Add a new admin user to the platform management team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input placeholder="admin@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as AdminRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex items-center gap-2">
                        <r.icon className="h-3.5 w-3.5" />
                        {r.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={!email.trim() || addAdmin.isPending}>
              {addAdmin.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformTeam;
